import type { ShallowReactive, UnwrapNestedRefs, UnwrapRef } from "vue"

import type { AnySchema, ArraySchema, InferOutput, ObjectSchema, RecordSchema } from "valibot"
import { getDefault, pick, safeParse, unwrap } from "valibot"
import { type Composer } from "vue-i18n"

import { isParsableArray } from "@finq/ui/utils/ui.utils"

import type { FieldSchemaMapping, PossibleReturnType } from "./shared"
import { useArrayField } from "./useArrayField"
import { useField } from "./useField"

export type PossibleSchema =
  | AnySchema
  | ObjectSchema<any, any>
  | ArraySchema<any, any>
  | RecordSchema<any, any, any>

export interface UseFormProps<TSchema extends ObjectSchema<any, any>> {
  defaultValues?: MaybeRefOrGetter<Partial<InferOutput<TSchema>>>
}

/**
 * Creates a form object with validation capabilities based on a given schema.
 * @param schema - The schema object used for validation.
 * @returns An object containing various form-related functions and properties.
 */
export function useForm<TSchema extends ObjectSchema<any, any>>(
  schema: MaybeRef<TSchema>,
  props: UseFormProps<TSchema> = {}
): UseFormReturnType<TSchema> {
  const { defaultValues = ref(undefined) } = toRefs(reactive(props))
  const schemaAsRef = toRef(schema)
  const schemaUnwrapped = computed((): TSchema extends { wrapped: infer U } ? U : TSchema =>
    "wrapped" in schemaAsRef.value ? unwrap(schemaAsRef.value as any) : (schemaAsRef.value as TSchema)
  )
  const fields = shallowReactive<FieldSchemaMapping<TSchema>>({} as any)

  // Init fields
  watch(
    [schemaAsRef, defaultValues],
    () => {
      const newFields = initFields({
        schema: schemaUnwrapped.value,
        defaultValues: defaultValues.value,
      })

      Object.assign(fields, newFields)

      // Validate prefilled fields
      nextTick(() => {
        for (const [key, field] of Object.entries(fields)) {
          if (field?.type === "field" && field?.state?.value !== undefined && field?.state?.value !== "") {
            field.validate()
          }
        }
      })
    },
    { immediate: true }
  )

  function fixStateField(refValue: any): any {
    const value = toValue(refValue)

    switch (true) {
      case typeof value === "string" && !value.length:
      case typeof value === "object" && !Array.isArray(value) && !Object.keys(value || {})?.length:
        return undefined

      case isParsableArray(value) || Array.isArray(value):
        return Object.values(value).map(fixStateField).filter(Boolean)

      default:
        return toRef(refValue)
    }
  }

  const state = reactive({} as InferOutput<TSchema>)
  const computedState = reactiveComputed((): InferOutput<TSchema> => {
    return Object.fromEntries(
      Object.entries(fields).map(([key, field]) => [key, fixStateField(field.state)])
    ) as InferOutput<TSchema>
  })

  // Workaround for Vue 3 reactivity issue, Sync state with computed state
  watch(computedState, (value) => Object.assign(state, value), { deep: true, immediate: true })

  const isValid = computed((): boolean => {
    return Object.values(fields).every((field) => field.isValid.value)
  })

  const eagerValid = computed(() => {
    return Object.values(fields).every((field) => field.eagerValid.value)
  })

  const errors = computed(() => {
    return Object.values(fields).flatMap((field) => field.errors.value)
  })

  const getField: UseFormReturnType<TSchema>["getField"] = (fieldName) => {
    return fields?.[fieldName]
  }

  const getErrors: UseFormReturnType<TSchema>["getErrors"] = (fieldName) => {
    if (!fields[fieldName] || !("errors" in fields[fieldName])) return []

    return fields[fieldName]?.errors.value || []
  }

  const getErrorMessage: UseFormReturnType<TSchema>["getErrorMessage"] = (fieldName) => {
    if (!fields[fieldName] || !("errorMessage" in fields[fieldName])) return ""

    return fields[fieldName]?.errorMessage.value
  }

  const translateErrorMessage: UseFormReturnType<TSchema>["translateErrorMessage"] = (fieldName, i18nT) => {
    const errorMessage = getErrorMessage(fieldName)
    if (!i18nT || !errorMessage) return errorMessage
    return i18nT(errorMessage)
  }

  const registerField: UseFormReturnType<TSchema>["registerField"] = (fieldName) => {
    if (!fields[fieldName] || !("binds" in fields[fieldName])) return {} as any

    return fields[fieldName]?.binds.value
  }

  const validateField: UseFormReturnType<TSchema>["validateField"] = (fieldName) => {
    if (!fields[fieldName] || !("validate" in fields[fieldName])) return false

    return fields[fieldName]?.validate() || false
  }

  const resetField: UseFormReturnType<TSchema>["resetField"] = (fieldName) => {
    if (!fields[fieldName] || !("reset" in fields[fieldName])) return

    return fields[fieldName]?.reset()
  }

  const setFieldErrors: UseFormReturnType<TSchema>["setFieldErrors"] = (fieldName, ...errors) => {
    if (!fields[fieldName] || !("setErrors" in fields[fieldName])) return

    return fields[fieldName]?.setErrors(...errors)
  }

  const clearFieldErrors: UseFormReturnType<TSchema>["clearFieldErrors"] = (fieldName) => {
    if (!fields[fieldName] || !("clearErrors" in fields[fieldName])) return

    return fields[fieldName]?.clearErrors()
  }

  const setFieldState: UseFormReturnType<TSchema>["setFieldState"] = (
    fieldName,
    value,
    validateState = true
  ) => {
    if (!fields[fieldName] || !("setState" in fields[fieldName])) return

    if (fields[fieldName]?.type === "field") {
      fields[fieldName]?.setState(value as any, validateState)
    } else {
      fields[fieldName]?.setState(value as any)
    }
  }

  const setState = (newState: InferOutput<TSchema>, validateState = true): void => {
    for (const key in fields) {
      setFieldState(key, newState[key], validateState)
    }
  }

  const validate = (): boolean => {
    for (const key in fields) {
      if (!fields[key] || !("validate" in fields[key])) continue

      fields[key]?.validate()
    }

    return isValid.value
  }

  const reset = (): void => {
    for (const key in fields) {
      if (!fields[key] || !("reset" in (fields[key] ?? {}))) continue

      fields[key]?.reset()
    }
  }

  const clearErrors = (): void => {
    for (const key in fields) {
      if (!fields[key] || !("clearErrors" in fields[key])) continue

      fields[key].clearErrors()
    }
  }

  const partiallyEagerValid: UseFormReturnType<TSchema>["partiallyEagerValid"] = (fields) => {
    const pickedFields = pick(schemaAsRef.value, fields as any)
    const stateRef = toRef(state)
    const result = safeParse(pickedFields as TSchema, stateRef.value)

    return result.success
  }

  const setValues = (values: Partial<InferOutput<TSchema>>): void => {
    for (const [key, value] of Object.entries(deepToRaw(values) || {})) {
      const field = fields[key as keyof InferOutput<TSchema>]

      if (!field) continue

      if (field.type === "array" && "syncValue" in field) {
        field.syncValue(value as any)
      } else if ("setState" in field) {
        field.setState(value)
      }

      // Making sure that the state is updated
      Object.assign(state, { [key]: value })
    }
  }
  return {
    type: "form",

    state,

    fields,
    isValid,
    eagerValid,
    errors,

    registerField,
    getField,
    getErrors,
    getErrorMessage,
    translateErrorMessage,

    validate,
    reset,
    clearErrors,

    validateField,
    resetField,
    setFieldErrors,
    clearFieldErrors,
    setFieldState,
    partiallyEagerValid,
    setValues,
    setState,
  }
}

function initFields<TSchema extends PossibleSchema>({
  schema,
  defaultValues,
  prefix = "",
}: {
  schema: TSchema
  defaultValues: any
  prefix?: string
  isNested?: boolean
}): Record<string, any> {
  const schemaUnwrapped = "wrapped" in schema ? unwrap(schema as any) : schema

  if (schema.type === "object" && "entries" in schema) {
    const fields: Record<string, any> = {}

    for (const [key, valueSchema] of Object.entries(schema.entries) as [string, PossibleSchema][]) {
      const fullKey = prefix ? `${prefix}.${key}` : key
      const defaultValue = defaultValues?.[key]
      const innerSchemaUnwrapped = "wrapped" in valueSchema ? unwrap(valueSchema as any) : valueSchema

      if (innerSchemaUnwrapped.type === "object") {
        fields[key] = useForm(valueSchema as ObjectSchema<any, any>, { defaultValues: defaultValue })
      } else {
        fields[key] = initFields({ schema: valueSchema, defaultValues: defaultValue, prefix: fullKey })
      }
    }

    return fields
  } else if (schema.type === "array" || schemaUnwrapped.type === "array") {
    return useArrayField({
      schema: schemaUnwrapped,
      name: prefix,
      defaultValue: defaultValues ?? getDefault(schema),
    })
  } else {
    return useField({ schema, name: prefix, defaultValue: defaultValues })
  }
}

/**
 * Represents the return type of the `useForm` composable function.
 * @template TSchema - The type of the object schema.
 */
export type UseFormReturnType<TSchema extends ObjectSchema<any, any>> = {
  /**
   * The type of the form.
   */
  type: "form"

  /**
   * The reactive state object that holds the form data.
   */
  state: UnwrapNestedRefs<InferOutput<TSchema>>

  /**
   * An object that contains field bindings for each field in the form.
   * The keys are the field names and the values are the field bindings.
   */
  fields: ShallowReactive<FieldSchemaMapping<TSchema>>

  /**
   * A reactive boolean ref that indicates whether the form is valid or not.
   */
  isValid: ComputedRef<boolean>

  /**
   * A reactive boolean ref that indicates whether the form should be eagerly validated or not.
   */
  eagerValid: ComputedRef<boolean>

  /**
   * An array of error messages for the form.
   */
  errors: ComputedRef<string[]>

  /**
   * A function that returns a field object for a specific field.
   * @param fieldName - The name of the field.
   * @returns A field object for the specified field.
   */
  getField: (fieldName: keyof InferOutput<TSchema>) => PossibleReturnType<TSchema>

  /**
   * A function that returns an array of error messages for a specific field.
   * @param fieldName - The name of the field.
   * @returns An array of error messages for the specified field.
   */
  getErrors: (fieldName: keyof InferOutput<TSchema>) => string[]

  /**
   * A function that returns the first error message for a specific field.
   * @param fieldName - The name of the field.
   * @returns The first error message for the specified field.
   */
  getErrorMessage: (fieldName: keyof InferOutput<TSchema>) => string

  /**
   * A function that translates the error message of a specific field.
   * @param fieldName - The name of the field.
   * @param i18nT - The t function from the i18n instance.
   */
  translateErrorMessage: (fieldName: keyof InferOutput<TSchema>, i18nT: Composer["t"]) => string

  /**
   * A function that validates the entire form and returns a boolean indicating whether the form is valid or not.
   * @returns A boolean indicating whether the form is valid or not.
   */
  validate: () => boolean

  /**
   * A function that resets the form to its initial state.
   */
  reset: () => void

  /**
   * A function that clears the errors of a specific field.
   * @param fieldName - The name of the field.
   */
  clearErrors: () => void

  /**
   * A function that validates a specific field and returns a boolean indicating whether the field is valid or not.
   * @param fieldName - The name of the field.
   * @returns A boolean indicating whether the field is valid or not.
   */
  validateField: (fieldName: keyof InferOutput<TSchema>) => boolean

  /**
   * A function that resets a specific field to its initial state.
   * @param fieldName - The name of the field.
   */
  resetField: (fieldName: keyof InferOutput<TSchema>) => void

  /**
   * A function that sets the errors of a specific field.
   * @param fieldName - The name of the field.
   * @param errors - The errors to set.
   */
  setFieldErrors: (fieldName: keyof InferOutput<TSchema>, ...errors: string[]) => void

  /**
   * A function that clears the errors of a specific field.
   * @param fieldName - The name of the field.
   */
  clearFieldErrors: (fieldName: keyof InferOutput<TSchema>) => void

  /**
   * A function that sets the state of a specific field.
   */
  setFieldState: <K extends keyof InferOutput<TSchema>>(
    fieldName: K,
    value: InferOutput<TSchema>[K],
    validateState?: boolean
  ) => void

  /**
   * Returns the bindings for a specific field.
   * @param fieldName - The name of the field.
   * @returns The field bindings for the element.
   */
  registerField: (fieldName: keyof InferOutput<TSchema>) => UnwrapRef<UseFieldReturnType<TSchema>["binds"]>

  /**
   * Returns the eager validity of picked fields.
   * @param fields - The fields to check for validity.
   */
  partiallyEagerValid: (fields: keyof InferOutput<TSchema>[]) => boolean

  /**
   * A function that updates multiple field values at once.
   * @param values - Partial form values to update
   */
  setValues: (values: Partial<InferOutput<TSchema>>) => void

  /**
   * A function that sets the state of the form.
   * @param newState - The new state to set.
   */
  setState: (newState: InferOutput<TSchema>, validateState?: boolean) => void
}
